<script lang="ts" module>
  import type { BlogPostStoryblok } from '~/component-types-sb';
  import { JsonLd } from '@agapi/ui/json-ld';

  export interface BlogPostProps {
    blok: BlogPostStoryblok;
    name: string;
    publishedAt: string;
    slug: string;
  }
</script>

<script lang="ts">
  import { storyblokEditable, renderRichText } from '@storyblok/svelte';
  import { page } from '$app/state';
  import { PUBLIC_BRAND_NAME } from '$env/static/public';
  import * as m from '$lib/parachute/messages';

  import { Section } from '@agapi/ui/section';
  import { Breadcrumb, BreadcrumbItem } from '@agapi/ui/breadcrumb';
  import { Badge } from '@agapi/ui/badge';
  import { Image } from '@agapi/ui/image';
  import { css } from '@agapi/ui/panda/css';

  import { formatDate, getLocaleFromUrl } from '../../../../utils/locale.shared';
  import { calculateReadingTime, parseIngress } from '../../../../utils/storyblok.client';
  import { blogPostStyles } from './blog-post.styles';

  import { richTextStyles } from '../../rich-text/rich-text.styles';
  import { BreadcrumbsSchema } from '../../../../components/breadcrumbs-schema';
  import { Author } from '../../../../components/author';

  const { blok, name, publishedAt, slug }: BlogPostProps = $props();

  const articleHTML = $derived(renderRichText(blok.body));
  const readingTime = calculateReadingTime(blok.body?.content);

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: blok.name,
    image: blok.image?.filename,
    datePublished: publishedAt,
    dateModified: publishedAt,
    author: {
      '@type': 'Person',
      name: blok.author?.name ?? PUBLIC_BRAND_NAME,
      description: blok.author?.content?.description ?? '',
      image: blok.author?.content?.image?.filename ?? ''
    },
    publisher: {
      '@type': 'Organization',
      name: PUBLIC_BRAND_NAME,
      logo: {
        '@type': 'ImageObject',
        url: `${page.url.origin}/logo.png`
      }
    },
    description: blok.meta_description || parseIngress(blok?.body?.content),
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${page.url.origin}/magazine/${slug}`
    }
  };

  const classes = blogPostStyles.raw();
</script>

<svelte:head>
  <title>{blok.meta_title || name} - {PUBLIC_BRAND_NAME} {m.blog()}</title>
  <meta name="description" content={blok.meta_description !== '' ? blok.meta_description : parseIngress(blok?.body?.content)} />
  <link rel="canonical" href={blok.canonical ?? `${page.url.origin}/magazine/${slug}`} />
  <meta property="og:title" content={blok.meta_title || name} />
  <meta property="og:description" content={blok.meta_description !== '' ? blok.meta_description : parseIngress(blok?.body?.content)} />
  <meta property="og:type" content="article" />
  <meta property="og:url" content={`${page.url.origin}/magazine/${slug}`} />
  <meta property="og:image" content={blok.meta_image ?? blok.image?.filename} />
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content={blok.meta_title || name} />
  <meta name="twitter:description" content={blok.meta_description !== '' ? blok.meta_description : parseIngress(blok?.body?.content)} />
  {#if blok.meta_image}
    <meta name="twitter:image" content={blok.meta_image ?? blok.image?.filename} />
  {/if}
</svelte:head>

<BreadcrumbsSchema
  items={[
    { name: m.blog(), item: `${page.url.origin}/magazine` },
    { name: blok.category[0].name, item: `${page.url.origin}/magazine/${blok.category[0].slug}` },
    { name: name, item: `${page.url.origin}/magazine/${slug}` },
  ]}
/>

<JsonLd data={schemaData} />

<div use:storyblokEditable={blok}>
  <Section as="header" containerWidth="slim" rootStyles={classes.headerSection} containerStyles={classes.headerContainer}>
    {#if blok.image}
      <Image
        src={blok.image.filename ?? ''}
        width={987}
        background={blok.image.filename + '/m/4x0'}
        aspectRatio={16 / 9}
        alt={blok.image.alt || name}
        css={classes.image}
      />
    {/if}
    <Breadcrumb>
      <BreadcrumbItem index={0}>
        <a href='/blog'>{m.blog()}</a>
      </BreadcrumbItem>
      {#if blok.category && blok.category.length > 0}
      <BreadcrumbItem index={1}>
        <a href={`/blog/${blok.category[0].slug}`}>{blok.category[0].name}</a>
      </BreadcrumbItem>
      {/if}
      <BreadcrumbItem index={2}>{name}</BreadcrumbItem>
    </Breadcrumb>
    <h1 class={css(classes.title)}>{name}</h1>
    <div class={css(classes.meta)}>
      <a href={'/blog/' + blok.category[0].slug}>
        <Badge>{blok.category[0].name}</Badge>
      </a>
      <span>{formatDate(publishedAt, getLocaleFromUrl(page.url))}</span>
      <span>•</span>
      <span>{readingTime} {m.minReading()}</span>
    </div>
  </Section>

  <Section as="article" containerWidth="tight">
    <div class={css(classes.content)}>
      {#if blok.body}
        <div class={css(richTextStyles, classes.richText)}>{@html articleHTML}</div>
      {/if}
    </div>
  </Section>

  {#if blok.author}
  <Author
    eyebrow={m.writtenBy()}
    name={blok.author.name}
    image={blok.author.content?.image?.filename}
    description={blok.author.content?.description || ''}
  />
{/if}
</div>
